import type {
  QuotePriceInternalType,
  QuotePriceType,
} from '@ui/features/quote-builder/types/state/quote-price';
import stringify from 'json-stringify-deterministic';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { QuoteBuilderInternalType } from '../../types/state/internal';
import { QuoteType } from '../../types/state/quote';
import {
  QuoteLineInternalType,
  QuoteLineType,
} from '../../types/state/quote-line';

type SerializedQuoteType = Omit<QuoteType, 'quoteLines'> &
  Record<keyof QuoteBuilderInternalType, undefined> & {
    quoteLines: Record<string, QuoteLineWithoutInternal>;
  };

type QuoteLineWithoutInternal = Omit<
  QuoteLineType,
  keyof QuoteLineInternalType | 'quotePrices'
> &
  Record<keyof QuoteLineInternalType, undefined> & {
    quotePrices: Record<string, QuotePriceWithoutInternal>;
  };

type QuotePriceWithoutInternal = Omit<
  QuotePriceType,
  keyof QuotePriceInternalType
> &
  Record<keyof QuotePriceInternalType, undefined>;

export function stringifyQuote(state: WritableQuoteBuilderStateType) {
  const quoteBuilderData = {
    ...state,

    // Don't include any of the keys from the QuoteLineInternalType
    quoteLines: Object.fromEntries(
      Object.entries(state.quoteLines).map(([quoteLineUuid, line]) => [
        quoteLineUuid,
        {
          ...line,

          // Exclude internal fields
          isInfoVisible: undefined,
          infoTab: undefined,
          infoTabOrderHistoryFilterByCustomer: undefined,
          infoTabQuoteHistoryFilterByCustomer: undefined,
          infoTabOrderHistoryExactMatch: undefined,
          infoTabQuoteHistoryExactMatch: undefined,
          status: undefined,
          productOptionsChangeUnitPrice: undefined,

          // handle quote prices
          quotePrices: Object.fromEntries(
            Object.entries(line.quotePrices).map(([quotePriceUuid, price]) => {
              return [
                quotePriceUuid,
                {
                  ...price,

                  // Exclude internal fields
                  status: undefined,
                },
              ];
            }),
          ),
        } satisfies QuoteLineWithoutInternal,
      ]),
    ),

    // Don't include any of the keys from the QuoteBuilderInternalType
    rawFieldValues: undefined,
    teamCode: undefined,
    dialog: undefined,
    partSearchColumns: undefined,
    issues: undefined,
    isIssueDrawerOpen: undefined,
    isLoading: undefined,
    isUpdating: undefined,
    quoteLineConfiguratorSnapshot: undefined,
    showAllDiscountCodes: undefined,
    showAllMarketCodes: undefined,
    currentServerData: undefined,
    pendingServerData: undefined,
    lastSaveDateTime: undefined,
  } satisfies SerializedQuoteType;

  return stringify(quoteBuilderData);
}
